import React from "react";
import { Drawer, Form, Select, Input, Divider, FormInstance } from "antd";
import { TransferItem, Store, Product } from "../../../Types";
import { TransferItemPicker } from "./TransferItemPicker";
import { TransferItemsList } from "./TransferItemsList";
import {
  DRAWER_DETAIL_WIDTH,
  drawerFooter,
  drawerTitle,
} from "../../../components";

const { Option } = Select;
const { TextArea } = Input;

interface Props {
  open: boolean;
  form: FormInstance;
  stores: Store[];
  fromStoreId: string | undefined;
  products: Product[];
  isProductsLoading: boolean;
  selectedProductId: string | null;
  selectedProduct: Product | null;
  selectedStockId: string | null;
  transferQty: number;
  transferItems: TransferItem[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onProductSearch: (v: string) => void;
  onProductChange: (id: string) => void;
  onStockChange: (id: string) => void;
  onQtyChange: (qty: number) => void;
  onAddItem: () => void;
  onRemoveItem: (stockId: string) => void;
}

export const NewTransferDrawer: React.FC<Props> = ({
  open,
  form,
  stores,
  fromStoreId,
  products,
  isProductsLoading,
  selectedProductId,
  selectedProduct,
  selectedStockId,
  transferQty,
  transferItems,
  isSubmitting,
  onClose,
  onSubmit,
  onProductSearch,
  onProductChange,
  onStockChange,
  onQtyChange,
  onAddItem,
  onRemoveItem,
}) => (
  <Drawer
    title={drawerTitle("New Stock Transfer")}
    open={open}
    onClose={onClose}
    width={DRAWER_DETAIL_WIDTH}
    footer={drawerFooter({
      actions: [
        { label: "Cancel", onClick: onClose },
        {
          label: "Execute Transfer",
          type: "primary",
          loading: isSubmitting,
          onClick: onSubmit,
          disabled: transferItems.length === 0,
        },
      ],
    })}
  >
    <Form form={form} layout="vertical">
      <div className="grid grid-cols-2 gap-3">
        <Form.Item
          name="from_store_id"
          label="Source Store"
          rules={[{ required: true, message: "Select source store." }]}
        >
          <Select placeholder="Select store">
            {stores.map((s) => (
              <Option key={s.id} value={s.id}>
                {s.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="to_store_id"
          label="Destination Store"
          rules={[
            { required: true, message: "Select destination store." },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("from_store_id") !== value)
                  return Promise.resolve();
                return Promise.reject(
                  new Error("Destination cannot be the same as source."),
                );
              },
            }),
          ]}
        >
          <Select placeholder="Select store">
            {stores.map((s) => (
              <Option key={s.id} value={s.id}>
                {s.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      <Form.Item name="note" label="Note">
        <TextArea
          rows={2}
          placeholder="Transfer reason, reference, etc."
          maxLength={500}
        />
      </Form.Item>

      {fromStoreId && (
        <>
          <Divider className="my-3" />

          <div className="mb-4">
            <TransferItemPicker
              products={products}
              isProductsLoading={isProductsLoading}
              selectedProductId={selectedProductId}
              selectedProduct={selectedProduct}
              selectedStockId={selectedStockId}
              transferQty={transferQty}
              onProductSearch={onProductSearch}
              onProductChange={onProductChange}
              onStockChange={onStockChange}
              onQtyChange={onQtyChange}
              onAdd={onAddItem}
            />
          </div>

          <TransferItemsList items={transferItems} onRemove={onRemoveItem} />
        </>
      )}
    </Form>
  </Drawer>
);
